import type { GetServerSideProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { signIn } from "next-auth/react";
import { getServerSession } from "@calcom/features/auth/lib/getServerSession";
import { useState } from "react";
import { Zap, AlertCircle } from "lucide-react";

export default function RwgBridgeSignup() {
  const router = useRouter();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Generate username from email
    const username = email
      .split("@")[0]
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")
      .slice(0, 20) + Math.floor(Math.random() * 9000 + 1000);

    // Create the user account
    const signupRes = await fetch("/api/auth/signup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, email, password, language: "en", name }),
    });

    if (!signupRes.ok) {
      const data = await signupRes.json().catch(() => ({}));
      setError(data.message ?? "Failed to create account. The email may already be in use.");
      setLoading(false);
      return;
    }

    // Sign in immediately
    const result = await signIn("credentials", {
      email,
      password,
      redirect: false,
      callbackUrl: "/rwg-bridge/onboarding",
    });

    if (result?.error) {
      setError("Account created but sign-in failed. Please go to the login page and sign in.");
      setLoading(false);
      return;
    }

    router.push("/rwg-bridge/onboarding");
  };

  return (
    <>
      <Head>
        <title>Create account · RWG Bridge</title>
        <meta name="robots" content="noindex,nofollow" />
      </Head>

      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="mb-8 flex flex-col items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 shadow-lg shadow-blue-200">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <div className="text-center">
              <h1 className="text-xl font-bold text-gray-900">Create your account</h1>
              <p className="mt-1 text-sm text-gray-500">Get on Reserve with Google in minutes</p>
            </div>
          </div>

          {/* Pricing callout */}
          <div className="mb-5 flex items-center justify-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm">
            <span className="font-semibold text-blue-800">$19 / location / month</span>
            <span className="text-blue-500">·</span>
            <span className="text-blue-700">No trial · Cancel anytime</span>
          </div>

          {/* Card */}
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            {error && (
              <div className="mb-5 flex items-start gap-2.5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Full name</label>
                <input
                  type="text"
                  required
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  placeholder="Jane Smith"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Work email</label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  placeholder="jane@acmespa.com"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  placeholder="Min. 8 characters"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-2 w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-60 transition-colors">
                {loading ? "Creating account…" : "Create account & continue"}
              </button>
            </form>

            <p className="mt-5 text-center text-xs text-gray-400">
              Already have an account?{" "}
              <a href="/rwg-bridge/login" className="text-blue-600 hover:underline">
                Sign in
              </a>
            </p>

            <p className="mt-3 text-center text-xs text-gray-400">
              By creating an account you agree to our{" "}
              <a href="https://schedly.io/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">
                Terms of Service
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ req, res }) => {
  const session = await getServerSession({ req: req as Parameters<typeof getServerSession>[0]["req"], res });
  if (session?.user?.id) {
    return { redirect: { destination: "/rwg-bridge/dashboard", permanent: false } };
  }
  return { props: {} };
};
